import React, { useState } from "react";
import { CharacterCreator } from "./CharacterCreator.js";
import { PartyStore } from "../../store/PartyStore.js";
import { gameActor } from "../hooks/useGameMachine.js";
import { defaultPartyName } from "../../engine/persistence/SaveFile.js";
import { mintPartyId } from "../../engine/party/partyIdentity.js";
import type { Character } from "../../engine/types/entity.js";

/**
 * Wave 2 new-game flow (C13, extended in P18).
 *
 * Loops {@link CharacterCreator} 1–4 times. Each completion appends to
 * `PartyStore`; "Start adventure" stamps a fresh `partyId`/`partyName`
 * on `PartyStore` and sends `PARTY_READY` to the game machine.
 *
 * If `PartyStore` already has members on mount (save-load path, or a
 * pre-seeded reuse-party cohort), we skip straight to the party-name
 * step — identity may already be set, but the BetweenPanel lets the
 * player rename before committing.
 */
const MAX_SLOTS = 4;

export function NewGameFlow() {
  const [slotCount, setSlotCount] = useState(() => PartyStore.getMembers().length);
  const [phase, setPhase] = useState<"creator" | "between">(
    PartyStore.getMembers().length > 0 ? "between" : "creator",
  );

  const handleComplete = (character: Character) => {
    PartyStore.addMember(character);
    const next = slotCount + 1;
    setSlotCount(next);
    setPhase("between");
  };

  const startAdventure = (partyName: string) => {
    const existingId = PartyStore.getPartyId();
    const id = existingId ?? mintPartyId();
    PartyStore.setPartyIdentity(id, partyName);
    gameActor.send({ type: "PARTY_READY" });
  };

  const addAnother = () => {
    setPhase("creator");
  };

  return (
    <div style={backdropStyle}>
      {phase === "creator" ? (
        <CharacterCreator
          key={slotCount}
          onComplete={handleComplete}
          {...(slotCount > 0
            ? {
                onCancel: () => {
                  setPhase("between");
                },
              }
            : {})}
        />
      ) : (
        <BetweenPanel
          slotCount={slotCount}
          onAddAnother={addAnother}
          onStart={startAdventure}
        />
      )}
    </div>
  );
}

function BetweenPanel({
  slotCount,
  onAddAnother,
  onStart,
}: {
  slotCount: number;
  onAddAnother: () => void;
  onStart: (partyName: string) => void;
}) {
  const atCap = slotCount >= MAX_SLOTS;
  const members = PartyStore.getMembers();
  const suggestedName = React.useMemo(
    () => PartyStore.getPartyName() ?? defaultPartyName(members.map((m) => m.name)),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- members array identity churns; we only want the initial suggestion.
    [],
  );
  const [partyName, setPartyName] = useState(suggestedName);
  const trimmed = partyName.trim();
  const canStart = slotCount > 0 && trimmed.length > 0;

  return (
    <div style={panelStyle}>
      <div style={{ fontSize: 18, marginBottom: 4 }}>Party roster</div>
      <div style={{ color: "rgba(255,255,255,0.6)", fontSize: 12, marginBottom: 16 }}>
        {slotCount} of {MAX_SLOTS} characters ready
      </div>
      <ul style={{ listStyle: "none", padding: 0, margin: "0 0 16px 0" }}>
        {members.map((m) => (
          <li key={m.instanceId} style={rowStyle}>
            <span>{m.name}</span>
            <span style={{ color: "rgba(255,255,255,0.5)", fontSize: 11 }}>
              L{m.level} {m.characterClass}
            </span>
          </li>
        ))}
      </ul>
      <label style={{ display: "flex", flexDirection: "column", gap: 4, marginBottom: 16 }}>
        <span style={{ fontSize: 11, color: "rgba(255,255,255,0.6)" }}>PARTY NAME</span>
        <input
          type="text"
          value={partyName}
          onChange={(e) => setPartyName(e.target.value)}
          maxLength={48}
          style={inputStyle}
          data-testid="party-name-input"
        />
      </label>
      <div style={{ display: "flex", gap: 8 }}>
        <button
          type="button"
          onClick={onAddAnother}
          disabled={atCap}
          style={{ ...btnStyle, opacity: atCap ? 0.4 : 1 }}
        >
          Add another
        </button>
        <div style={{ flex: 1 }} />
        <button
          type="button"
          onClick={() => onStart(trimmed)}
          disabled={!canStart}
          style={{ ...btnStyle, background: canStart ? "#2a6" : "#333", opacity: canStart ? 1 : 0.5 }}
          data-testid="start-adventure-btn"
        >
          Start adventure
        </button>
      </div>
    </div>
  );
}

const backdropStyle: React.CSSProperties = {
  position: "absolute",
  inset: 0,
  background: "rgba(0,0,0,0.85)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  pointerEvents: "auto",
  zIndex: 400,
};

const panelStyle: React.CSSProperties = {
  background: "#1a1a1a",
  border: "1px solid rgba(255,255,255,0.2)",
  borderRadius: 8,
  padding: 24,
  color: "white",
  fontFamily: "monospace",
  width: 420,
  display: "flex",
  flexDirection: "column",
  pointerEvents: "auto",
};

const rowStyle: React.CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  padding: "6px 10px",
  background: "#222",
  border: "1px solid rgba(255,255,255,0.1)",
  borderRadius: 4,
  marginBottom: 4,
  fontSize: 13,
};

const btnStyle: React.CSSProperties = {
  padding: "8px 16px",
  background: "#333",
  color: "white",
  border: "1px solid rgba(255,255,255,0.2)",
  borderRadius: 4,
  fontFamily: "monospace",
  fontSize: 13,
  cursor: "pointer",
};

const inputStyle: React.CSSProperties = {
  padding: "8px 10px",
  background: "#111",
  color: "white",
  border: "1px solid rgba(255,255,255,0.2)",
  borderRadius: 4,
  fontFamily: "monospace",
  fontSize: 13,
};
